import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Alert,
  AlertTitle,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import WebIcon from '@mui/icons-material/Web';
import RefreshIcon from '@mui/icons-material/Refresh';
import { trpc } from '../../lib/trpc';
import { StatusPageRow, StatusPageRowSkeleton } from '../../components/status-pages/StatusPageRow';

/**
 * Get plan name from limit value
 */
function getPlanName(limit: number): string {
  if (limit === Infinity || limit === -1) return 'Enterprise';
  if (limit <= 1) return 'Free';
  if (limit <= 3) return 'Starter';
  if (limit <= 10) return 'Pro';
  return 'Enterprise';
}

/**
 * Plan limit info component
 */
function PlanLimitInfo({ current, limit }: { current: number; limit: number }) {
  const isUnlimited = limit === Infinity || limit === -1;
  const percentage = isUnlimited ? 0 : (current / limit) * 100;
  const isApproachingLimit = percentage >= 80;
  const isAtLimit = current >= limit && !isUnlimited;
  const planName = getPlanName(limit);

  return (
    <Paper sx={{ p: 2, mb: 3, backgroundColor: 'background.default' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Status Pages Usage
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {current} / {isUnlimited ? 'Unlimited' : limit} ({planName} Plan)
        </Typography>
      </Box>
      {!isUnlimited && (
        <LinearProgress
          variant="determinate"
          value={Math.min(percentage, 100)}
          color={isAtLimit ? 'error' : isApproachingLimit ? 'warning' : 'primary'}
          sx={{ height: 6, borderRadius: 1 }}
        />
      )}
      {isAtLimit && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          You have reached your status page limit.{' '}
          <Link component={RouterLink} to="/settings" underline="hover">
            Upgrade your plan
          </Link>{' '}
          to create more.
        </Typography>
      )}
    </Paper>
  );
}

/**
 * Table header component
 */
function TableHeader() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1.5, sm: 2 },
        px: 2,
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'action.hover',
      }}
    >
      {/* Visibility indicator */}
      <Box sx={{ width: 10 }} />

      {/* Name */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          minWidth: { xs: 100, sm: 150 },
          maxWidth: { xs: 150, sm: 250 },
        }}
      >
        Name
      </Typography>

      {/* Public URL */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          display: { xs: 'none', sm: 'block' },
          flex: 1,
          minWidth: 0,
        }}
      >
        Public URL
      </Typography>

      {/* Monitors */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          minWidth: 80,
          textAlign: 'right',
        }}
      >
        Monitors
      </Typography>

      {/* Actions placeholder */}
      <Box sx={{ width: 36, ml: 0.5 }} />
    </Box>
  );
}

/**
 * Loading skeleton for the list
 */
function ListSkeleton() {
  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <TableHeader />
      {Array.from({ length: 3 }).map((_, i) => (
        <StatusPageRowSkeleton key={i} />
      ))}
    </Paper>
  );
}

/**
 * Status pages list component
 * Displays all public status pages
 */
export function StatusPagesListPage() {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pageToDelete, setPageToDelete] = useState<{ id: string; name: string } | null>(null);

  // Fetch status pages
  const {
    data: statusPages,
    isLoading,
    error,
    refetch,
    isFetching,
  } = trpc.statusPages.list.useQuery();

  // Fetch plan limits
  const limitsQuery = trpc.statusPages.limits.useQuery();

  // Delete mutation
  const deleteMutation = trpc.statusPages.delete.useMutation({
    onSuccess: () => {
      refetch();
      limitsQuery.refetch();
      setDeleteDialogOpen(false);
      setPageToDelete(null);
    },
  });

  const handleDeleteClick = (id: string, name: string) => {
    setPageToDelete({ id, name });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (pageToDelete) {
      deleteMutation.mutate({ id: pageToDelete.id });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPageToDelete(null);
  };

  // Check if user can create more status pages
  // Default to false when data isn't available to prevent accidental creation
  const canCreate = limitsQuery.data
    ? limitsQuery.data.limit === -1 ||
      limitsQuery.data.limit === Infinity ||
      limitsQuery.data.current < limitsQuery.data.limit
    : false;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Status Pages
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your public status pages
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton
              onClick={() => {
                refetch();
                limitsQuery.refetch();
              }}
              disabled={isFetching}
              sx={{ border: 1, borderColor: 'divider' }}
            >
              <RefreshIcon
                sx={{
                  animation: isFetching ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
            </IconButton>
          </Tooltip>
          <Button
            component={RouterLink}
            to="/status-pages/new"
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!canCreate}
          >
            Create Status Page
          </Button>
        </Box>
      </Box>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error loading status pages</AlertTitle>
          {error.message}
        </Alert>
      )}

      {/* Plan Limits Info */}
      {limitsQuery.data && (
        <PlanLimitInfo current={limitsQuery.data.current} limit={limitsQuery.data.limit} />
      )}

      {/* Loading State */}
      {isLoading ? (
        <ListSkeleton />
      ) : statusPages && statusPages.length === 0 ? (
        /* Empty State */
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <WebIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No status pages yet
          </Typography>
          <Typography
            variant="body2"
            color="text.disabled"
            sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}
          >
            Create a public status page to keep your users informed about service availability and
            incidents.
          </Typography>
          <Button
            component={RouterLink}
            to="/status-pages/new"
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!canCreate}
          >
            Create Your First Status Page
          </Button>
        </Paper>
      ) : (
        /* Status Pages List */
        <Paper sx={{ overflow: 'hidden' }}>
          <TableHeader />
          {statusPages?.map((page) => (
            <StatusPageRow
              key={page.id}
              statusPage={{
                id: page.id,
                name: page.name,
                slug: page.slug,
                description: page.description,
                isPublic: page.isPublic,
                monitorCount: page.monitorCount,
              }}
              onDelete={(id) => {
                const pageData = statusPages.find((p) => p.id === id);
                if (pageData) {
                  handleDeleteClick(id, pageData.name);
                }
              }}
            />
          ))}
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Status Page</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the status page &quot;{pageToDelete?.name}&quot;? This
            action cannot be undone. The public URL will no longer be accessible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteMutation.isLoading}
          >
            {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default StatusPagesListPage;
